import { EventEmitter, Inject, Injectable, signal } from '@angular/core';
import { isInIframe } from '@app/helpers/iframe-helper';
import { TrackLoginSuccessParams } from '@app/models/tracking/track-event-param-type';
import { ResetLoginParams } from '@app/models/tracking/track-history-param-type';
import { delayHelper } from '@commonHelpers/delay-helper';
import { isNullish, isUndefined } from '@commonHelpers/math-utils';
import type { EventType } from '@azure/msal-browser';
import { AUTH_EVENTS, AUTH_EVENT_ACQUIRE_TOKEN_FAILURE, AUTH_EVENT_LOGIN_SUCCESS, AUTH_EVENT_LOGOUT_SUCCESS, AUTH_PROGRESS_NONE } from "@commonInterfaces/iAuthAccess";
import type { IAuthEvents } from "@commonInterfaces/iAuthAccess";
import { environment } from '@environments/environment';
import { AccountAuthorizationDisplayInfoDto, AccountLanguage, EntityRoleAssignmentDisplayInfoDto, Role, RolesDto, UserMenuDisplayInfoDto } from '@interfaces/HttpClient/AccountApiPublicModels';
import { timer } from 'rxjs';
import { AccountService } from './account.service';
import { AnalyticsTrackingFacadeService } from './analytics-tracking-facade.service';
import { ApplicationInsightsService } from './application-insights.service';
import { GlobalVarService } from './global-var.service';
import { SoftwareIntegrationCallbackService } from './software-integration-callback.service';

export interface AuthState {
	loaded: boolean;
	loggedIn: boolean;
}

@Injectable({
	providedIn: "root"
})
export class UserAuthorizationService {
	private msalLoading = true;
	private readonly authStateSignal = signal<AuthState>({ loaded: false, loggedIn: false });
	public readonly authState = this.authStateSignal.asReadonly();

	private accountAuthorizationDisplayInfos: AccountAuthorizationDisplayInfoDto;
	private effectiveRoles: RolesDto;
	private defaultDisplayMenu: UserMenuDisplayInfoDto;
	private userMenuDisplayCatalogIdInfo = new Map<number, UserMenuDisplayInfoDto>();
	private userMenuDisplayKeyInfo = new Map<string, UserMenuDisplayInfoDto>();
	private userMenuDisplayCompanyInfo = new Map<number, UserMenuDisplayInfoDto>();
	public refreshLanguageUserMenu = new EventEmitter<AccountLanguage>();
	public refreshMenu = new EventEmitter<void>();
	public initWatchlist = new EventEmitter<void>();
	public loginFailure = new EventEmitter<void>();
	public refreshComponents = new EventEmitter<void>();
	private loadingEmit = new EventEmitter<void>();
	private currentLoginEventType: EventType;

	constructor(
		private accountService: AccountService,
		@Inject(AUTH_EVENTS) private authEvents: IAuthEvents,
		private aiService: ApplicationInsightsService,
		private trackingFacadeService: AnalyticsTrackingFacadeService,
		private globalVarService: GlobalVarService,
		private softwareIntegrationCallbackService: SoftwareIntegrationCallbackService
	) {
	}

	clearData(): void {
		this.accountAuthorizationDisplayInfos = undefined;
		this.effectiveRoles = undefined;
		this.userMenuDisplayKeyInfo = new Map<string, UserMenuDisplayInfoDto>();
		this.userMenuDisplayCatalogIdInfo = new Map<number, UserMenuDisplayInfoDto>();
		this.userMenuDisplayCompanyInfo = new Map<number, UserMenuDisplayInfoDto>();
		this.defaultDisplayMenu = undefined;

	}

	isLoggedIn(): boolean {
		return this.accountService.isLoggedIn();
	}

	async hasRole(role: Role): Promise<boolean> {
		if (!await this.ensureAccountInfosLoaded()) {
			return false;
		}
		return this.effectiveRoles?.roles.includes(role);
	}

	public isLoading() {
		return this.msalLoading
	}

	subscribeState() {

		this.authEvents.events$.subscribe(async subject => {
			// Ereignisnamen nach dem MSAL-Sprung auf msal-browser 5: LOGIN_FAILURE wurde durch
			// ACQUIRE_TOKEN_FAILURE ersetzt, ACCOUNT_ADDED durch LOGIN_SUCCESS und
			// ACCOUNT_REMOVED durch LOGOUT_SUCCESS (so in der offiziellen v5-Migration
			// festgelegt). Dadurch fallen die früher getrennten Zweige zusammen - der
			// LOGOUT_SUCCESS-Zweig unten deckt jetzt ab, was vorher ACCOUNT_REMOVED tat,
			// weshalb die dortige Doppelung von clearPersistedLoginHint/clearData entfällt.
			//
			// Kein Zweig für 'access_denied: AADB2C90118' mehr: Azure AD B2C führt das
			// Zurücksetzen des Kennworts inzwischen als Claims-Exchange innerhalb der
			// Anmelde-Policy aus ("Kennwort vergessen?" verweist auf
			// CombinedSigninAndSignup/unified?claimsexchange=ForgotPassword&p=B2C_1_signup_signin).
			// Der Fehler erreicht die Anwendung dadurch nicht mehr, weshalb die
			// pwdreset-Policy hier ersatzlos entfallen ist.
			if (subject.eventType === AUTH_EVENT_LOGIN_SUCCESS || subject.eventType === AUTH_EVENT_LOGOUT_SUCCESS || subject.eventType === AUTH_EVENT_ACQUIRE_TOKEN_FAILURE) {
				this.currentLoginEventType = subject.eventType;
			}
			if (subject.eventType === AUTH_EVENT_LOGOUT_SUCCESS) {
				this.accountService.clearPersistedLoginHint();
				// Die Rollen- und Menü-Caches gehören zum abgemeldeten Konto und dürfen
				// nicht weiterverwendet werden
				this.clearData();
			}
			// Setzt den Zustand direkt am Ereignis, weil inProgress$ nach einem Login oder
			// Logout nicht zwangsläufig erneut auf None läuft.
			// Kein Cross-Tab-Mechanismus: die abgelösten Ereignisse ACCOUNT_ADDED und
			// ACCOUNT_REMOVED wurden in msal-browser 4 aus BrowserCacheManager.setAccount
			// im schreibenden Tab emittiert, nicht aus einem storage-Listener. Ein Login in
			// einem anderen Tab hat diesen Zweig also auch vorher nicht erreicht.
			// Nach einem ssoSilent-Wiederaufbau feuert LOGIN_SUCCESS nicht - dort setzt
			// inProgress$ -> handleLogin() den Zustand (siehe unten).
			if (subject.eventType === AUTH_EVENT_LOGIN_SUCCESS || subject.eventType === AUTH_EVENT_LOGOUT_SUCCESS) {
				const loggedIn = this.isLoggedIn();
				this.authStateSignal.set({ loaded: !this.msalLoading, loggedIn });
				// Nur im angemeldeten Zustand: Abonnenten wie der Admin-Header erzwingen sonst einen interaktiven Login
				if (loggedIn) {
					this.refreshMenu.next();
				}
			}
		})

		if (isInIframe()) {
			this.accountService.refreshStartInvokeIframe().subscribe(() => this.msalLoading = true);
			this.accountService.refreshEndInvokeIframe().subscribe(async () => this.handleLogin())
		}

		this.authEvents.progress$.subscribe(async progress => {
			if (progress === AUTH_PROGRESS_NONE) {
				if (!isInIframe() && await this.accountService.trySilentSsoRestore()) {
					// ssoSilent führt inProgress$ wieder auf None -> handleLogin läuft mit finalem Zustand genau einmal
					return;
				}
				await this.handleLogin();
			}
		});
		if (environment.needsAdditionalAuthenticationInformation) {
			delayHelper(async () => {
				const needsAuthentication = await this.accountService.getNeedsAuthentication();
				if (needsAuthentication) {
					timer(5000).subscribe(async () => {
						if (!this.isLoggedIn()) {
							await this.accountService.login()
						}
					})
				}
			})

		}

	}

	private async handleLogin() {
		await this.trackingFacadeService.initTracking();
		this.handleLoginStatus();
		await this.globalVarService.accountRedirectHandle();
		this.loadingEmit.next(null);
		this.loadingEmit.complete();
		this.softwareIntegrationCallbackService.msalLoaded();
		this.msalLoading = false;
		this.authStateSignal.set({ loaded: true, loggedIn: this.isLoggedIn() });
		await this.softwareIntegrationCallbackService.startSession();
		if (this.isLoggedIn()) {
			const language = await this.accountService.getLanguage();
			if (!isNullish(language)) {
				this.globalVarService.setLanguage(language);
				this.globalVarService.setTmpLanguage(language);
			}
			this.accountService.persistLoginHint();
			this.refreshMenu.next();
		} else if (environment.needsAdditionalAuthenticationInformation) {
			const needsAuthentication = await this.accountService.getNeedsAuthentication();
			if (needsAuthentication) {
				await this.accountService.login();
			}
		}
		this.initWatchlist.emit();
	}

	// Es darf erst auf Sessions zugegriffen werden wenn der msalprozess abgeschlossen wurde und die 'Session gesetzt wurde.
	private handleLoginStatus() {
		switch (this.currentLoginEventType) {
			case AUTH_EVENT_LOGIN_SUCCESS: {
				this.trackingFacadeService.handleTrackEvent(new TrackLoginSuccessParams())
				break;
			}
			case AUTH_EVENT_LOGOUT_SUCCESS: {
				this.trackingFacadeService.handleTrackHistory(new ResetLoginParams())
				break;
			}
			// Früher LOGIN_FAILURE; in msal-browser 5 durch ACQUIRE_TOKEN_FAILURE ersetzt.
			case AUTH_EVENT_ACQUIRE_TOKEN_FAILURE: {
				this.trackingFacadeService.handleTrackHistory(new ResetLoginParams())
				this.aiService.clearAuthenticatedUserContext();
				this.loginFailure.next();
				break;
			}
		}
	}

	async hasCatalogRole(catalogId: string, role: Role): Promise<boolean> {
		if (!await this.ensureAccountInfosLoaded()) {
			return false;
		}

		if (!this.effectiveRoles?.rolesByCatalogId) {
			return false;
		}

		const catalogRoles = this.effectiveRoles?.rolesByCatalogId?.[catalogId];

		if (!catalogRoles) {
			return false;
		}

		return catalogRoles.includes(role);
	}

	// Fuer Aufrufer, denen die Katalog-Id nur optional vorliegt: ohne Katalog-Kontext gibt es keine Berechtigung
	async hasCatalogRoleOrDefault(catalogId: number | null | undefined, role: Role): Promise<boolean> {
		if (isNullish(catalogId)) {
			return false;
		}
		return this.hasCatalogRole(catalogId.toString(), role);
	}

	async hasCompanyRole(companyId: string, role: Role): Promise<boolean> {
		if (!await this.ensureAccountInfosLoaded()) {
			return false;
		}

		if (!this.effectiveRoles?.rolesByCompanyId) {
			return false;
		}

		const companyRoles = this.effectiveRoles?.rolesByCompanyId?.[companyId];

		if (!companyRoles) {
			return false;
		}

		return companyRoles.includes(role);
	}

	async getCatalogs(): Promise<EntityRoleAssignmentDisplayInfoDto[]> {
		if (!await this.ensureAccountInfosLoaded()) {
			return;
		}
		return this.accountAuthorizationDisplayInfos.activeCatalogSpecificRoles;
	}

	async getCompanies(): Promise<EntityRoleAssignmentDisplayInfoDto[]> {
		if (!await this.ensureAccountInfosLoaded()) {
			return Array();
		}
		return this.accountAuthorizationDisplayInfos.activeCompanySpecificRoles;
	}

	private async ensureAccountInfosLoaded(): Promise<boolean> {
		if (this.msalLoading) {
			await this.loadingEmit.toPromise();
		}
		if ((isNullish(this.accountAuthorizationDisplayInfos) || isNullish(this.effectiveRoles))) {
			await this.loadAccountInfos();
		}
		return true;
	}

	private async loadAccountInfos(): Promise<void> {
		if (this.msalLoading) {
			await this.loadingEmit.toPromise();
		}
		if (this.isLoggedIn()) {
			this.accountAuthorizationDisplayInfos = await this.accountService.getUserAuthorizationDisplayInfo();
			this.effectiveRoles = await this.accountService.getUserEffectiveRoles();
		} else {
			this.clearData();
		}

	}

	public async getMenuDisplayInfoDtoByCatalogKey(catalogKey: string): Promise<UserMenuDisplayInfoDto> {
		let userMenu = await this.initUserMenu();
		if (isUndefined(userMenu)) {
			if (!this.userMenuDisplayKeyInfo.has(catalogKey)) {
				this.userMenuDisplayKeyInfo.set(catalogKey, await this.accountService.getUserMenuDisplayInfoByCatalogKey(catalogKey));
			}
			userMenu = this.setDefaultUserMenu(this.userMenuDisplayKeyInfo.get(catalogKey))
		}
		return userMenu;
	}

	public async getMenuDisplayInfo(): Promise<UserMenuDisplayInfoDto> {
		let userMenu = await this.initUserMenu();
		if (isUndefined(userMenu)) {
			if (isNullish(this.defaultDisplayMenu)) {
				this.defaultDisplayMenu = await this.accountService.getUserMenuDisplayInfo();
			}
			userMenu = this.setDefaultUserMenu(this.defaultDisplayMenu);
		}
		return userMenu;
	}

	public async getEffectiveRoles(): Promise<Role[]> {
		if (!await this.ensureAccountInfosLoaded()) {
			return undefined;
		}
		return this.effectiveRoles?.roles;
	}

	private setDefaultUserMenu(userMenu: UserMenuDisplayInfoDto) {
		if (!userMenu?.isAdmin) {
			this.defaultDisplayMenu = userMenu;
		}
		return userMenu;
	}

	private async initUserMenu() {
		if (!isNullish(this.defaultDisplayMenu) && !this.defaultDisplayMenu.isAdmin) {
			return this.defaultDisplayMenu;
		}
		return undefined;
	}
}
